import React from 'react';
import { Link } from 'react-router-dom';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const RefundPolicyPage: React.FC = () => {
  useDocumentTitle('Refund Policy');

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc' }}>
      {/* Header */}
      <div className="border-bottom bg-white">
        <div className="container py-3">
          <div className="d-flex align-items-center justify-content-between">
            <Link to="/" className="text-decoration-none d-flex align-items-center">
              <div style={{
                width: 40, height: 40,
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>C</span>
              </div>
              <span className="ms-2 fw-bold" style={{
                fontSize: '1.2rem',
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>CloudAyn</span>
            </Link>
            <Link to="/" className="btn btn-outline-primary btn-sm">Back to Home</Link>
          </div>
        </div>
      </div>

      <div className="container py-5" style={{ maxWidth: '800px' }}>
        <h1 className="fw-bold mb-2">Refund Policy</h1>
        <p className="text-muted mb-4">Last updated: February 2026</p>

        <div className="card border-0 shadow-sm p-4 p-md-5" style={{ lineHeight: '1.7' }}>
          <section className="mb-4">
            <h4 className="fw-bold mb-3">1. Overview</h4>
            <p>
              This Refund Policy outlines the terms governing refunds for subscription payments made to
              INFIYO TECHNOLOGIES WLL ("Company"), operating under the brand name CloudAyn. By subscribing
              to any CloudAyn plan, you acknowledge and agree to the terms of this Refund Policy.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">2. No Refund Policy</h4>
            <p>
              <strong>All subscription payments are non-refundable.</strong> Once a subscription is activated
              and payment has been processed, no refunds will be issued for the remaining duration of the
              subscription period, regardless of usage.
            </p>
            <p>This applies to:</p>
            <ul>
              <li>Monthly subscription payments</li>
              <li>Yearly subscription payments</li>
              <li>Plan upgrades or downgrades</li>
              <li>Payments made via credit/debit card, PayPal, or bank transfer</li>
            </ul>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">3. Free Trial</h4>
            <p>
              CloudAyn offers free trial periods on select paid plans. During the trial period, you are not
              charged. No payment information is required to start a free trial. The trial allows you to
              evaluate the platform before committing to a paid subscription.
            </p>
            <p>
              At the end of the trial period, your subscription will not automatically convert to a paid
              plan. You must explicitly choose to subscribe and make a payment to continue using paid features.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">4. Subscription Cancellation</h4>
            <p>
              You may cancel your subscription at any time through your account settings. Upon cancellation:
            </p>
            <ul>
              <li>Your subscription remains active until the end of the current billing period</li>
              <li>You will continue to have access to paid features until the period expires</li>
              <li>No prorated refunds will be issued for the unused portion of the billing period</li>
              <li>After the billing period ends, your account will revert to the Free plan</li>
            </ul>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">5. Plan Changes</h4>
            <p>
              <strong>Upgrades:</strong> If you upgrade to a higher-tier plan, the new pricing takes effect
              immediately. The difference in pricing may be charged at the time of upgrade.
            </p>
            <p>
              <strong>Downgrades:</strong> If you downgrade to a lower-tier plan, the change takes effect
              immediately. No refund or credit will be issued for the price difference.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">6. Service Disruptions</h4>
            <p>
              In the event of extended, unplanned service outages caused by us (exceeding 72 consecutive hours),
              we may, at our sole discretion, offer service credits to affected subscribers. Service credits
              are applied to future billing periods and are not redeemable for cash.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">7. Billing Disputes</h4>
            <p>
              If you believe you have been incorrectly charged, please contact us within 30 days of the
              charge at info@cloudayn.com. We will review your case and, if an error is confirmed, issue
              a correction or credit as appropriate.
            </p>
            <p>
              Initiating a chargeback or payment dispute through your bank or payment provider without
              first contacting us may result in immediate suspension of your account.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">8. Exceptional Circumstances</h4>
            <p>
              We reserve the right to issue refunds at our sole discretion in exceptional circumstances.
              Such decisions are made on a case-by-case basis and do not set a precedent for future requests.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">9. Taxes</h4>
            <p>
              Refunds, if issued under exceptional circumstances, will include any applicable taxes (such as
              VAT) that were charged at the time of the original payment.
            </p>
          </section>

          <section>
            <h4 className="fw-bold mb-3">10. Contact Us</h4>
            <p>
              For any questions regarding this Refund Policy or to report a billing issue, please contact us:
            </p>
            <ul className="list-unstyled">
              <li><strong>Company:</strong> INFIYO TECHNOLOGIES WLL</li>
              <li><strong>Brand:</strong> CloudAyn</li>
              <li><strong>Email:</strong> info@cloudayn.com</li>
              <li><strong>Location:</strong> Kingdom of Bahrain</li>
            </ul>
          </section>
        </div>

        {/* Footer links */}
        <div className="text-center mt-4">
          <Link to="/terms" className="text-muted me-3">Terms & Conditions</Link>
          <Link to="/privacy" className="text-muted">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
